import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Star, TrendingUp, TrendingDown, Users, MousePointer,
  BarChart3, ArrowLeft, Shield, ExternalLink, Percent
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

const AnalyticsPage = () => {
  const { user, isImpersonating } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30);

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics?days=${timeRange}`);
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!analytics) return null;

  // Prepare chart data
  const ratingTrendData = analytics.ratings_by_day.map(day => ({
    date: day.date.slice(5), // MM-DD format
    ratings: day.count,
    average: day.average
  }));

  const platformData = analytics.ratings_by_platform.map(p => ({
    name: p.platform,
    ratings: p.ratings,
    clicks: p.clicks,
    average: p.average
  }));

  return (
    <div className="min-h-screen bg-[var(--color-bg-subtle)] py-8 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 text-[var(--color-text-secondary)] hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to dashboard
          </Link>
          {user?.is_admin && !isImpersonating && (
            <Link 
              to="/admin"
              className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 text-sm font-medium transition-colors"
              data-testid="admin-dashboard-link"
            >
              <Shield className="w-4 h-4" />
              Admin Dashboard
            </Link>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-dashboard-heading text-2xl sm:text-3xl font-bold tracking-tight">
              Analytics
            </h1>
            <p className="text-[var(--color-text-secondary)] mt-1">
              Track your ratings and follow performance
            </p>
          </div>
          
          <Tabs value={String(timeRange)} onValueChange={(v) => setTimeRange(Number(v))}>
            <TabsList>
              <TabsTrigger value="7">7 days</TabsTrigger>
              <TabsTrigger value="30">30 days</TabsTrigger>
              <TabsTrigger value="90">90 days</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[var(--color-text-muted)] uppercase tracking-wide font-medium">
                    Total Ratings
                  </p>
                  <p className="text-3xl font-dashboard-heading font-bold mt-1" data-testid="analytics-total-ratings">
                    {analytics.total_ratings}
                  </p>
                </div>
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-[var(--color-accent)]" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[var(--color-text-muted)] uppercase tracking-wide font-medium">
                    Avg Rating
                  </p>
                  <p className="text-3xl font-dashboard-heading font-bold mt-1" data-testid="analytics-avg-rating">
                    {analytics.average_rating > 0 ? analytics.average_rating : '—'}
                  </p>
                </div>
                <div className="w-10 h-10 bg-yellow-50 rounded-xl flex items-center justify-center">
                  <Star className="w-5 h-5 text-yellow-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[var(--color-text-muted)] uppercase tracking-wide font-medium">
                    Positive
                  </p>
                  <p className="text-3xl font-dashboard-heading font-bold mt-1 text-green-600" data-testid="analytics-positive">
                    {analytics.positive_ratings}
                  </p>
                </div>
                <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[var(--color-text-muted)] uppercase tracking-wide font-medium">
                    Negative
                  </p>
                  <p className="text-3xl font-dashboard-heading font-bold mt-1 text-amber-600" data-testid="analytics-negative">
                    {analytics.negative_ratings}
                  </p>
                </div>
                <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[var(--color-text-muted)] uppercase tracking-wide font-medium">
                    Click-throughs
                  </p>
                  <p className="text-3xl font-dashboard-heading font-bold mt-1 text-blue-600" data-testid="analytics-clickthroughs">
                    {analytics.review_clickthroughs || 0}
                  </p>
                </div>
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                  <ExternalLink className="w-5 h-5 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[var(--color-text-muted)] uppercase tracking-wide font-medium">
                    Conversion
                  </p>
                  <p className="text-3xl font-dashboard-heading font-bold mt-1 text-emerald-600" data-testid="analytics-conversion">
                    {analytics.clickthrough_rate > 0 ? `${analytics.clickthrough_rate}%` : '—'}
                  </p>
                </div>
                <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <Percent className="w-5 h-5 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[var(--color-text-muted)] uppercase tracking-wide font-medium">
                    Follow Clicks
                  </p>
                  <p className="text-3xl font-dashboard-heading font-bold mt-1 text-purple-600" data-testid="analytics-clicks">
                    {analytics.total_follow_clicks}
                  </p>
                </div>
                <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
                  <MousePointer className="w-5 h-5 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Rating Trend */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-dashboard-heading text-lg font-semibold">
                Rating Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={ratingTrendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#A3A3A3" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#A3A3A3" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e5e5e5',
                        borderRadius: '8px'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="ratings" 
                      stroke="#2563EB" 
                      strokeWidth={2}
                      dot={{ fill: '#2563EB', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Platform Performance */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-dashboard-heading text-lg font-semibold">
                Platform Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={platformData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 12 }} stroke="#A3A3A3" />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} stroke="#A3A3A3" width={100} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e5e5e5',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="ratings" fill="#2563EB" radius={[0, 4, 4, 0]} name="Ratings" />
                    <Bar dataKey="clicks" fill="#10B981" radius={[0, 4, 4, 0]} name="Clicks" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Platform Breakdown Table */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-dashboard-heading text-lg font-semibold">
              Platform Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Platform</th>
                    <th>Ratings</th>
                    <th>Avg Rating</th>
                    <th>Follow Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {platformData.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-8 text-[var(--color-text-muted)]">
                        No data yet
                      </td>
                    </tr>
                  ) : (
                    platformData.map((platform, index) => (
                      <tr key={platform.name}>
                        <td className="font-medium">{platform.name}</td>
                        <td>{platform.ratings}</td>
                        <td>
                          <div className="flex items-center gap-1">
                            {platform.average > 0 ? platform.average : '—'}
                            {platform.average > 0 && (
                              <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                            )}
                          </div>
                        </td>
                        <td>{platform.clicks}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AnalyticsPage;
